import { Either, Validation } from 'monet';

/**
 * @description Профиль пользователя.
 * @param {string} email
 * @param {string} name
 * @param {string} patronymic
 * @param {string} surname
 * @param {string} photoUrl
 */
export function UserProfile(
  email = '',
  name = '',
  patronymic = '',
  surname = '',
  photoUrl = '',
) {
  this.email = email;
  this.name = name;
  this.patronymic = patronymic;
  this.surname = surname;
  this.photo_url = photoUrl;
}

/**
 * @description Запрос на редактирование профиля пользователя.
 * @param {UserProfile} profile Профиль пользователя.
 */
export function EditUserProfileRequest(profile) {
  this.toQuery = () => ({
    email: profile.email,
    name: profile.name,
    patronymic: profile.patronymic,
    surname: profile.surname,
  });
}
/**
 * @description Шлюз пользователей.
 * @param {AxiosStatic} axios Http клиент.
 * @constructor
 */
export default function UserGateway(axios) {
  /**
   * @description Получить данные профиля пользователя.
   * @returns {Promise<Validation>}
   */
  this.viewProfile = async () => {
    try {
      const response = await axios.get('/api/profile');
      const { profile, ip } = response.data;
      const userProfile = new UserProfile(
        profile.email,
        profile.name,
        profile.patronymic,
        profile.surname,
        profile.photo_url,
      );
      return Validation.Success({ profile: userProfile, ipAddress: ip });
    } catch (error) {
      if (error.isAxiosError) {
        return Validation.Fail(error.response.data);
      }
      throw error;
    }
  };

  /**
   * @description Отправить запрос на редактирование профиля пользователя.
   * @param {EditUserProfileRequest} request
   * @returns {Promise<Validation>}
   */
  this.edit = async (request) => {
    try {
      const response = await axios.put('/api/profile', request.toQuery());
      return Validation.Success(response.data);
    } catch (error) {
      if (error.isAxiosError) {
        return Validation.Fail(error.response.data);
      }
      throw error;
    }
  };

  /**
   * @description Отправить запрос на добавление фотографии
   * @param {FormData} form
   * @return {Promise<Validation<unknown, any>|Validation<T, unknown>>}
   */
  this.uploadPhoto = async (form) => {
    try {
      const response = await axios.post('/api/profile/photo', form);
      return Either.Right(response.data);
    } catch (error) {
      if (error.isAxiosError) {
        return Either.Left(error.response.data);
      }
      throw error;
    }
  };

  /**
   * @description Изменить филиал пользователя.
   * @param {number} clinicId
   * @returns {Promise<Either>}
   */
  this.changeUserBranch = (clinicId) => axios
    .post(`/api/user/branch-clinics/${clinicId}`)
    .then(
      async (response) => Either.right({
        token: response.data.token,
      }),
      async (error) => {
        if (error.isAxiosError && [403, 404].includes(error.response.status)) {
          return Either.left(error.response.data.title);
        }
        throw error;
      },
    );

  /**
   * @description Отправить запрос на закрытия всплывающего окна удобства работы с ПК.
   * мобильного приложения.
   * @returns {Promise}
   */
  this.closeWorkOnPCMobilePopup = () => axios
    .put('/api/close-work-on-pc-mobile-popup')
    .then((response) => response.data.data);

  /**
   * @description Сохранить предложенную идею.
   * @param {string} description Описание.
   * @returns {Promise<Either>}
   */
  this.storeIdea = (description) => axios
    .post('/api/suggested-ideas', { description })
    .then(
      async (response) => Either.right(response.data.data.message),
      async (error) => Either.left(error.response.data.title),
    );
  /**
   * @description Получить филиалы пользователя.
   * @returns {Promise<Either>}
   */
  this.getBranchedClinics = () => axios
    .get('/api/user/branch-clinics')
    .then(
      async (response) => Either.right({
        branchedClinics: response.data.branched_clinics,
        selectedBranchId: response.data.selected_branch_id,
      }),
      async (error) => Either.left(error.response.data.title),
    );

  /**
   * @description Показать приветственною статистику.
   * @returns {Promise<Either>}
   */
  this.viewWelcome = () => axios.get('/api/welcome').then(
    (response) => Either.right(response.data),
    (error) => {
      if (error.isAxiosError && error.response.status === 403) {
        return Either.left(error.response.data);
      }
      throw error;
    },
  );
}
